import { useState, useRef, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import useAxios from '../../api/axios';
import TextField from '@mui/material/TextField';
import { Button as AppButton } from '../../components/Button';
import { Link } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

const LOGIN_URL = "login?action=login";

const sx = { backgroundColor: 'white', borderRadius: '12px' };

const Login = () => {
  const { setAuth } = useAuth();
  const api = useAxios();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from
    ? `${location.state.from.pathname || ''}${location.state.from.search || ''}${location.state.from.hash || ''}` || '/'
    : '/';

  const errRef = useRef();
  const [username, setUsername] = useState('');
  const [pwd, setPwd] = useState('');
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => { setErrMsg(''); }, [username, pwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await api.post(
        LOGIN_URL,
        JSON.stringify({ action: 'login', email: username, password: pwd }),
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );
      const accessToken = response?.data?.token;
      const user = response?.data?.user;
      setAuth({ user, accessToken });
      setUsername('');
      setPwd('');
      navigate(from, { replace: true });
    } catch (err) {
      if (!err?.response) {
        toast.error('Ei vastausta palvelimelta');
      } else if (err.response?.status === 401) {
        toast.error(err.response.data.msg);
      } else {
        toast.error('Kirjautuminen epäonnistui');
      }
      errRef.current.focus();
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100dvh-4rem)] max-w-sm flex-col justify-center px-4 py-4 sm:py-8">
      <div className="mb-3 rounded-[24px] border border-white/10 bg-slate-900/70 p-4 shadow-2xl backdrop-blur sm:mb-6 sm:rounded-[28px] sm:p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-cyan-300 sm:text-sm sm:tracking-[0.35em]">Tili</p>
        <h1 className="mt-1.5 text-2xl font-black text-white sm:mt-2 sm:text-3xl">Kirjaudu</h1>
        <p className="mt-1.5 text-sm leading-5 text-slate-300 sm:mt-2 sm:leading-6">Kirjaudu sisään osallistuaksesi kilpailuihin.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="rounded-[24px] border border-white/10 bg-slate-900/70 p-4 shadow-2xl backdrop-blur sm:rounded-[28px] sm:p-6">
          <p ref={errRef} className={errMsg ? 'mb-4 rounded-xl bg-red-500/20 px-4 py-2 text-sm text-red-300' : 'sr-only'} aria-live="assertive">
            {errMsg}
          </p>

          <div className="grid gap-3 sm:gap-4">
            <TextField
              variant="filled"
              required
              label="Sähköposti"
              type="email"
              autoComplete="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={sx}
              fullWidth
            />
            <TextField
              variant="filled"
              required
              label="Salasana"
              type="password"
              autoComplete="current-password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              sx={sx}
              fullWidth
            />
          </div>

          <div className="mt-3 space-y-1.5 text-center text-xs text-slate-400 sm:mt-4 sm:space-y-2 sm:text-sm">
            <div>
              <Link to="/uusi-salasana" className="hover:text-slate-200 underline">
                Unohtuiko salasana?
              </Link>
            </div>
            <div>
              Ei vielä tunnusta?{' '}
              <Link to="/register" className="font-semibold text-cyan-300 hover:text-cyan-200">
                Rekisteröidy tästä
              </Link>
            </div>
          </div>

          <div className="mt-5 flex justify-end gap-3 sm:mt-6">
            <AppButton name="Takaisin" type="SECONDARY" action={() => navigate('/')} />
            <AppButton name="Kirjaudu" type="PRIMARY" buttonType="submit" />
          </div>
        </div>
      </form>
    </div>
  );
};

export default Login;
